import React, { useState, useEffect } from 'react';
import { UUIDs } from '../ble';
import DeviceConnection from '../DeviceConnection';
import ProtobufObjectTable from '../ProtobufObjectTable';
import ProtobufCollectionTable from './ProtobufCollectionTable';
import Cookies from 'js-cookie';
import { useProtobufDeviceContext } from './ProtobufDeviceContext';
import './protobufDevice.css';

function ProtobufDevice({ onCharacteristicsChange, onConfigUpdate }) {
  const [collapsedStates, setCollapsedStates] = useState(() => {
    // Initialize collapsed states from cookies
    const savedStates = Cookies.get('collapsedStates');
    return savedStates ? JSON.parse(savedStates) : {};
  });
  const [isSchemaCollapsed, setIsSchemaCollapsed] = useState(() => {
    // Initialize schema collapse state from cookies
    const savedSchemaState = Cookies.get('isSchemaCollapsed');
    return savedSchemaState ? JSON.parse(savedSchemaState) : false;
  });

  // Use the shared protobuf device context
  const {
    deviceConnectionState,
    isDeviceConnected,
    connectionState,
    protobufSchema,
    protobufCharacteristics,
    protobufSchemaRoot,
    protobufSchemaProtocol,
    protobufRoot,
    isInitialized,
    handleConnect,
    handleDisconnect,
    handleConnectionStateChange,
    protobufProtocolService
  } = useProtobufDeviceContext();

  useEffect(() => {
    // Save collapsed states to cookies whenever they change
    Cookies.set('collapsedStates', JSON.stringify(collapsedStates), { expires: 7 });
  }, [collapsedStates]);

  useEffect(() => {
    // Save schema collapse state to cookies whenever it changes
    Cookies.set('isSchemaCollapsed', JSON.stringify(isSchemaCollapsed), { expires: 7 });
  }, [isSchemaCollapsed]);

  useEffect(() => {
    if (onCharacteristicsChange) {
      onCharacteristicsChange(protobufCharacteristics);
    }
  }, [protobufCharacteristics, onCharacteristicsChange]);

  const handleCharacteristicChange = (index, key, newValue) => {
    protobufProtocolService.updateCharacteristicValue(index, key, newValue);
  };

  const pushChanges = async (index) => {
    await protobufProtocolService.pushChanges(index);
    
    // If this is a device config characteristic and we have an onConfigUpdate callback
    if (onConfigUpdate && protobufCharacteristics[index]?.protobufType === 'DeviceConfig') {
      onConfigUpdate(protobufCharacteristics[index].value);
    }
  };

  const toggleCollapse = (index) => {
    setCollapsedStates(prevStates => ({
      ...prevStates,
      [index]: !prevStates[index]
    }));
  };

  const toggleSchemaCollapse = () => {
    setIsSchemaCollapsed(!isSchemaCollapsed);
  };

  const handleAutoUpdateChange = (index, autoUpdate) => {
    protobufProtocolService.setAutoUpdate(index, autoUpdate);
  };

  const handleDeviceConnect = async () => {
    // Connect to the protobuf service after device connection
    try {
      await handleConnect();
    } catch (error) {
      console.error('Error connecting to protobuf service:', error);
    }
  };

  const handleDeviceDisconnect = () => {
    handleDisconnect();
  };

  // Generate and log schema JSON when characteristics and schema are ready
  useEffect(() => {
    if (protobufSchema && protobufCharacteristics.length > 0) {
      // eslint-disable-next-line no-unused-vars -- AUD-DEP-13-LINT: pre-existing write-only/unused binding in the legacy BLE+compass tree. Removing it is a behaviour question, not a lint fix; suppressed explicitly so no-unused-vars can stay an ERROR everywhere else.
      const schemaJson = protobufProtocolService.generateSchemaJson();
      /*if (schemaJson) {
        console.log('Device Schema JSON:', JSON.stringify(schemaJson, null, 2));
      }*/
    }
  }, [protobufSchema, protobufCharacteristics, protobufProtocolService]);

  if (!isInitialized) {
    return <div>Initializing Protobuf Protocol Service...</div>;
  }

  if (!protobufRoot) {
    return <div>Loading Protobuf...</div>;
  }

  const containerClass = `
    state-characteristic-container 
    ${connectionState.connectionLost ? 'disconnected' : ''} 
    ${!isDeviceConnected && !connectionState.connectionLost ? 'offline' : ''}
  `;

  return (
    <div className={containerClass.trim()}>
      <DeviceConnection 
        onConnect={handleDeviceConnect} 
        onDisconnect={handleDeviceDisconnect}
        onConnectionStateChange={handleConnectionStateChange}
        ServiceUUID={UUIDs.protobufService}
        externalConnectionState={deviceConnectionState}
      />

      <div className="characteristic-data">
        <div>
          <h3>
            Schema
            <span
              className="toggle-button"
              onClick={toggleSchemaCollapse}
            >
              {isSchemaCollapsed ? '+' : '—'}
            </span>
          </h3>
          {!isSchemaCollapsed && protobufSchema && (
            <ProtobufObjectTable
              object={protobufSchema}
              typeName="ProtobufService"
              protobufRoot={protobufRoot}
              recursive={true}
              defaultIncrement={16}
            />
          )}
          {!protobufSchema && isDeviceConnected && (
            <div>Loading...</div>
          )}
        </div>
        <div>
          <h3>Values</h3>
          {protobufCharacteristics.map((characteristic, index) => {
            return (
              <div key={index}>
                <h4>
                  {characteristic.isCollection 
                    ? "Collection of " + characteristic.collectionData.recordProtobufType 
                    : characteristic.protobufType}
                  <span
                    className="toggle-button"
                    onClick={() => toggleCollapse(index)}
                  >
                    {collapsedStates[index] ? '+' : '—'}
                  </span>
                </h4>
                {!collapsedStates[index] && (
                  <>
                    {characteristic.isCollection ? (
                      // Render collection using ProtobufCollectionTable
                      <ProtobufCollectionTable
                        statusUuid={characteristic.collectionData.statusUuid}
                        recordUuid={characteristic.collectionData.recordUuid}
                        protobufType={characteristic.collectionData.recordProtobufType}
                        protobufRoot={protobufSchemaRoot}
                        protobufProtocolRoot={protobufSchemaProtocol}
                        editable={characteristic.writable}
                      />
                    ) : (
                      // Render regular characteristic using ProtobufObjectTable
                      <>
                        {characteristic.writable && (
                          <small>
                            <button
                              onClick={() => pushChanges(index)}
                              disabled={!characteristic.hasChanges}
                            >
                              Update
                            </button>
                            <label>
                              <input
                                type="checkbox"
                                checked={characteristic.autoUpdate}
                                onChange={() => handleAutoUpdateChange(index, !characteristic.autoUpdate)}
                              />
                              Update automatically
                            </label>
                          </small>
                        )}
                        {characteristic.value && (
                          <ProtobufObjectTable
                            object={characteristic.value}
                            typeName={characteristic.protobufType}
                            protobufRoot={protobufSchemaRoot}
                            editable={characteristic.writable}
                            onChange={(key, newValue) => handleCharacteristicChange(index, key, newValue)}
                            recursive={true}
                            numberTableColumns={6}
                            defaultIncrement={20}
                          />
                        )}
                        {!characteristic.value && isDeviceConnected && (
                          <div>Loading...</div>
                        )}
                      </>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default ProtobufDevice; 